import * as React from 'react';
import * as moment from 'moment';
import { useRef, useState } from 'react';
import { IDonorRegistration, IRemoteDonorRegistration } from '@nkr/models/index';
import { IMicrositeRecipientForListWithSelect } from '@nkr/views/microsite/list/models';
import { ColDef, ColumnApi, FirstDataRenderedEvent, GridApi, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button, Glyphicon, Modal, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { RemoteGridWrapper, RemoteTableWrapper } from '../styles';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import PopoverTemplate from '@nkr/components/popover-template';

interface IProps {
    show: boolean,
    data: IRemoteDonorRegistration,
    microsite: IMicrositeRecipientForListWithSelect,
    onCancel: () => void
}

const DonorRegistrationForRemotePatients: React.FC<IProps> = (props) => {
    if (props.data == null) {
        return null;
    }

    const ref = useRef();

    const [menuDisplayed, setMenuDisplayed] = useState(false);
    const [gridApi, setGridApi] = useState<GridApi>(null);
    const [columnApi, setColumnApi] = useState<ColumnApi>(null);
    
    const [columns, setColumns] = useState<ColDef[]>([
        {
            colId: 'rank',
            field: 'rank',
            headerName: 'Rank',
            headerComponentFramework: (props: IHeaderParams) =>
                <div>Rank</div>,
            sortable: false,
            width: 90,
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Center',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            width: 120,
            cellClass: 'remote-list-cell',
            headerClass: 'remote-list-header',
            sortable: true
        },
        {
            colId: 'donorName',
            field: 'donorName',
            headerName: 'Name',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Name</SortableHeaderComponent>,
            width: 160,
            cellClass: 'remote-list-cell',
            headerClass: 'remote-list-header',
            sortable: true
        },
        {
            colId: 'referralDate',
            field: 'referralDate',
            headerName: 'Referral Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Referral <br /> Date</SortableHeaderComponent>,
            width: 115,
            // maxWidth: 140,
            cellClass: 'remote-list-cell',
            headerClass: 'remote-list-header',
            sortable: true
        },
        {
            colId: 'coordinatorName',
            field: 'coordinatorName',
            headerName: 'Center',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Coordinator</SortableHeaderComponent>,
            width: 160,
            cellClass: 'remote-list-cell',
            headerClass: 'remote-list-header',
            sortable: true
        },
        {
            colId: 'coordinatorNumber',
            field: 'coordinatorNumber',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Coord #</SortableHeaderComponent>,
            width: 130,
            cellClass: 'remote-list-cell',
            headerClass: 'remote-list-header',
            sortable: true
        },
        {
            colId: 'coordinatorEmail',
            field: 'coordinatorEmail',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Coord<br />Email</SortableHeaderComponent>,
            width: 70,
            cellClass: 'remote-list-cell',
            headerClass: 'remote-list-header',
            sortable: true,
            cellRendererFramework: (props: ICellRendererParams) => {

                let email = props.data.coordinatorEmail
                return (
                    <a href={`mailto:${email}`}><Glyphicon glyph='envelope' /></a>
                );
            },
        },
        {
            colId: 'lastUpdatedDate',
            field: 'lastUpdatedDate',
            headerName: 'Last Updated',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last <br /> Updated</SortableHeaderComponent>,
            width: 115,
            cellClass: 'remote-list-cell',
            headerClass: 'remote-list-header',
            sortable: true
        },
        {
            colId: 'currentStatus',
            field: 'currentStatus',
            headerName: 'Recorded Status',
            headerComponentFramework: (params: IHeaderParams) => (
                <SortableHeaderComponent {...params}>
                    Recorded <br /> Status
                </SortableHeaderComponent>
            ),
            width: 270,
            cellClass: 'remote-list-cell',
            headerClass: 'remote-list-header',
            sortable: true,
            cellRendererFramework: (props: ICellRendererParams) => {
                const { currentStatusId, currentStatus, remoteStatusDate } = props.data;

                const data = props.data as IDonorRegistration;

                const statusMessages: { [key: number]: string } = {
                    100: 'Donated on -',
                    10: 'Workup Scheduled on -',
                    25: 'Pre-Op Scheduled on -'
                };

                const statusMessage = statusMessages[currentStatusId] ?? currentStatus;

                return (
                    <div>
                        {statusMessage}{' '}
                        {props.data.statusActivityHistory.length > 0 ? (
                            <OverlayTrigger trigger={['click', 'click']} placement='bottom' container={ref.current} rootClose overlay={
                                <PopoverTemplate width='800px'>
                                    <div style={{ fontWeight: 'bold', marginBottom: 5 }}>
                                        Status History List:
                                    </div>
                                    <Table striped bordered hover responsive>
                                        <thead>
                                            <tr style={{ backgroundColor: '#2460c2', color: 'white', fontWeight: 'bold' }}>
                                                <th style={{ textAlign: 'center', width: '20%' }}>Updated By</th>
                                                <th style={{ textAlign: 'center', width: '20%' }}>Coord First Name</th>
                                                <th style={{ textAlign: 'center', width: '20%' }}>Coord Last Name</th>
                                                <th style={{ textAlign: 'center', width: '20%' }}>Updated Date</th>
                                                <th style={{ textAlign: 'center', width: '20%' }}>Updated Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {props.data.statusActivityHistory.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{item.updatedByAlias}</td>
                                                    <td>{item.userFName}</td>
                                                    <td>{item.userLName}</td>
                                                    <td>{moment(item.updatedDate).format('MM/DD/YYYY')}</td>
                                                    <td>{item.updatedStatus}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </PopoverTemplate>
                            }>
                                <a style={{ marginLeft: 20, cursor: 'default' }}>click here for history detail</a>
                            </OverlayTrigger>) : ''}

                        {remoteStatusDate ? (
                            <b>({moment(remoteStatusDate).format('MM/DD/YYYY')})</b>
                        ) : (
                            ''
                        )}
                    </div>
                );
            },
        },
        {
            colId: 'remoteStatusDate',
            field: 'remoteStatusDate',
            headerName: 'Current Status Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Current <br /> Status Date</SortableHeaderComponent>,
            width: 180,
            cellClass: 'remote-list-cell',
            headerClass: 'remote-list-header',
            sortable: true
        },
        {
            colId: 'visibilityComments',
            field: 'visibilityComments',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Status<br />Comments</SortableHeaderComponent>,
            width: 100,
            cellClass: 'remote-list-cell',
            headerClass: 'remote-list-header',
            sortable: true,
            cellRendererFramework: (props: ICellRendererParams) => {
                const data = props.data as IDonorRegistration;

                const tooltip = (
                    <Tooltip id={`tooltip-${data.id}`} className='left-aligned-tooltip'>
                        {data.visibilityComments}
                    </Tooltip>
                );
                return (
                    data.visibilityComments != null ? (
                        <OverlayTrigger
                            placement='top'
                            trigger={['hover', 'click']}
                            overlay={tooltip}
                        >
                            <Glyphicon glyph='comment' style={{ color: '#327cb8' }} />
                        </OverlayTrigger>
                    )
                        : <></>
                );
            },
        },
    ]);

    const onGridReady = (e: GridReadyEvent) => {
        setGridApi(e.api);
        setColumnApi(e.columnApi);
        e.api.sizeColumnsToFit();
    }

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const getTitle = () => {
        if (props.microsite.centerId != null) {
            return (
                <>Registrations for your Microsite Intended Recipient (<span style={{ color: 'green' }}>{props.microsite.fullName}</span>)</>
            );
        }

        return <>Registrations for Microsite Intended Recipient (<span style={{ color: 'green' }}>{props.microsite.fullName}</span>)</>
    }

    const {
        inProgressCandidates: inProgressList, inProgressCandidatesAll: inProgressListAll,
        donatedCandidates: donatedList, donatedCandidatesAll: donatedListAll,
        ruledOutCandidates: ruledOutList, ruledOutCandidatesAll: ruledOutListAll
    } = props.data;

    const pendingReferralCount = props.data.registrationsRemotePatient.pendingReferral;
    const atYourCenterCount = props.data.registrationsRemotePatient.atYourCenter;
    const otherCentersCount = props.data.registrationsRemotePatient.otherCenters;

    const {
        inProgress: inProgressCount, inProgressAll: inProgressCountAll,
        ruledOut: ruledOutCount, ruledOutAll: ruledOutCountAll,
        donated: donatedCount, donatedAll: donatedCountAll
    } = props.data.statusAtOtherCenters;

    const ruleOutExcludedColumns = ['currentStatus', 'remoteStatusDate'];
    const inProgAndDonatedExcludedColumns = ['remoteStatusDate'];

    const ruleOutFilteredColumns = columns.filter((col) => !ruleOutExcludedColumns.includes(col.colId));
    const inProgressAndDonatedFilteredColumns = columns.filter((col) => !inProgAndDonatedExcludedColumns.includes(col.colId));

    return (
        <>
            <Modal
                className='modal-xxxl'
                bsSize='lg'
                animation={true}
                backdrop='static'
                show={props.show}
                onHide={props.onCancel}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {getTitle()}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className='row' style={{ marginBottom: 20 }}>
                        <div className='col-xs-6'>
                            <div className='row'>
                                <div className='col-xs-12 col-sm-12'>
                                    <h4>Registrations for Microsite Intended Recipient</h4>
                                </div>
                            </div>
                            <div className='row center-srtr'>
                                <div className='col-xs-12 col-sm-12'>
                                    <RemoteTableWrapper className='table table-condensed table-striped table-bordered'>
                                        <thead>
                                            <tr style={{ backgroundColor: '#133366', color: 'black', fontWeight: 'bold' }}>
                                                <th style={{ textAlign: 'center', width: '40%' }}>Pending Referrals and Rule Outs</th>
                                                <th style={{ textAlign: 'center', width: '30%' }}>At Your Center</th>
                                                <th style={{ textAlign: 'center', width: '30%' }}>Other Centers</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style={{ textAlign: 'center' }}>{pendingReferralCount}</td>
                                                <td style={{ textAlign: 'center' }}>{atYourCenterCount}</td>
                                                <td style={{ textAlign: 'center' }}>{otherCentersCount}</td>
                                            </tr>
                                        </tbody>
                                    </RemoteTableWrapper>
                                </div>
                            </div>
                        </div>
                        <div className='col-xs-6'>
                            <div className='row'>
                                <div className='col-xs-12 col-sm-12'>
                                    <h4>Status of Registrations {props.microsite.centerId != null ? 'at Other Centers' : ''}</h4>
                                </div>
                            </div>
                            <div className='row center-srtr'>
                                <div className='col-xs-12 col-sm-12'>
                                    <RemoteTableWrapper className='table table-condensed table-striped table-bordered'>
                                        <thead>
                                            <tr style={{ backgroundColor: '#022644', color: 'black', fontWeight: 'bold' }}>
                                                <th style={{ textAlign: 'center', width: '30%' }}>In Progress</th>
                                                <th style={{ textAlign: 'center', width: '30%' }}>Donated</th>
                                                <th style={{ textAlign: 'center', width: '30%' }}>Ruled Out</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style={{ textAlign: 'center' }}>{props.microsite.centerId != null ? inProgressCount : inProgressCountAll}</td>
                                                <td style={{ textAlign: 'center' }}>{props.microsite.centerId != null ? donatedCount : donatedCountAll}</td>
                                                <td style={{ textAlign: 'center' }}>{props.microsite.centerId != null ? ruledOutCount : ruledOutCountAll}</td>
                                            </tr>
                                        </tbody>
                                    </RemoteTableWrapper>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='row'>
                        <div className='col-xs-12'>
                            <div className='row'>
                                <div className='col-xs-12 col-sm-8'>
                                    <h4>In Progress Candidates {props.microsite.centerId != null ? 'at Other Centers' : ''}</h4>
                                </div>
                            </div>
                            <div className='row center-srtr'>
                                <div className='col-xs-12 col-sm-12'>
                                    <RemoteGridWrapper className='ag-theme-bootstrap' style={{ height: 200 }}>
                                        <AgGridReact
                                            columnDefs={inProgressAndDonatedFilteredColumns}
                                            rowData={props.microsite.centerId != null ? inProgressList : inProgressListAll}
                                            headerHeight={35}
                                            groupHeaderHeight={35}
                                            rowHeight={28}
                                            suppressCellSelection={true}
                                            suppressContextMenu={true}
                                            immutableData={false}
                                            noRowsOverlayComponentFramework={() => <div>No registrations found...</div>}
                                            onGridReady={onGridReady}
                                            onFirstDataRendered={onFirstDataRendered}
                                            onGridSizeChanged={(e: GridSizeChangedEvent) => e.api.refreshCells()}
                                        />
                                    </RemoteGridWrapper>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='row'>
                        <div className='col-xs-12'>
                            <div className='row'>
                                <div className='col-xs-12 col-sm-8'>
                                    <h4>Candidates who Donated {props.microsite.centerId != null ? 'at Other Centers' : ''}</h4>
                                </div>
                            </div>
                            <div className='row center-srtr'>
                                <div className='col-xs-12 col-sm-12'>
                                    <RemoteGridWrapper className='ag-theme-bootstrap' style={{ height: 200 }}>
                                        <AgGridReact
                                            columnDefs={inProgressAndDonatedFilteredColumns}
                                            rowData={props.microsite.centerId != null ? donatedList : donatedListAll}
                                            headerHeight={35}
                                            groupHeaderHeight={35}
                                            rowHeight={28}
                                            suppressCellSelection={true}
                                            suppressContextMenu={true}
                                            immutableData={false}
                                            noRowsOverlayComponentFramework={() => <div>No registrations found...</div>}
                                            onGridReady={onGridReady}
                                            onFirstDataRendered={onFirstDataRendered}
                                            onGridSizeChanged={(e: GridSizeChangedEvent) => e.api.refreshCells()}
                                        />
                                    </RemoteGridWrapper>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='row'>
                        <div className='col-xs-12'>
                            <div className='row'>
                                <div className='col-xs-12 col-sm-8'>
                                    <h4>Candidates who were Ruled Out {props.microsite.centerId != null ? 'at Other Centers' : ''}</h4>
                                </div>
                            </div>
                            <div className='row center-srtr'>
                                <div className='col-xs-12 col-sm-12'>
                                    <RemoteGridWrapper className='ag-theme-bootstrap' style={{ height: 200 }}>
                                        <AgGridReact
                                            columnDefs={ruleOutFilteredColumns} // Exclude 'currentStatus'
                                            rowData={props.microsite.centerId != null ? ruledOutList : ruledOutListAll}
                                            headerHeight={35}
                                            groupHeaderHeight={35}
                                            rowHeight={28}
                                            suppressCellSelection={true}
                                            suppressContextMenu={true}
                                            immutableData={false}
                                            noRowsOverlayComponentFramework={() => <div>No registrations found...</div>}
                                            onGridReady={onGridReady}
                                            onFirstDataRendered={onFirstDataRendered}
                                            onGridSizeChanged={(e: GridSizeChangedEvent) => e.api.refreshCells()}
                                        />
                                    </RemoteGridWrapper>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={props.onCancel}>OK</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default DonorRegistrationForRemotePatients;